import { useState } from 'react';
import { Download, BookOpen, Users, TestTube, Package, FileText, Lock, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Icon from '../components/AppIcon';
import { jsPDF } from 'jspdf';


const Help = () => {
  const [downloadFormat, setDownloadFormat] = useState('pdf');

  const handleDownload = () => {
    // Create manual content
    const manualContent = `
CryoTracker User Manual
Version 1.0
Last Updated: November 24, 2025

========================================
TABLE OF CONTENTS
========================================
1. Introduction
2. Getting Started
3. User Management
4. Sample Management
5. Dewar Management
6. Reports
7. Settings
8. Troubleshooting
9. Support

========================================
1. INTRODUCTION
========================================
CryoTracker is a comprehensive laboratory sample tracking system designed for managing cryogenic storage containers (dewars) and biological samples. The system provides robust user management, sample tracking, and reporting capabilities.

Key Features:
• User authentication and approval system
• Dewar (storage container) management
• Sample tracking and organization
• Comprehensive reporting
• Administrator controls
• Secure data management

========================================
2. GETTING STARTED
========================================

2.1 Account Registration
1. Navigate to the sign-up page
2. Enter your email address
3. Create a secure password
4. Provide your full name
5. Click "Sign Up"
6. Wait for administrator approval

2.2 Account Approval Process
• After registration, your account will be in "pending approval" status
• An administrator will review your request
• You'll receive access once approved
• Contact your lab administrator if approval is delayed

2.3 First Login
1. Go to the login page
2. Enter your approved email and password
3. Click "Sign In"
4. You'll be directed to the Dashboard

========================================
3. USER MANAGEMENT
========================================

3.1 User Roles
• Administrator: Full system access, can approve users
• Regular User: Can manage samples and dewars, view reports

3.2 For Administrators - Approving Users
1. Navigate to the Users page
2. Click on "Pending Approval" tab
3. Review user information
4. Click "Approve" to grant access 5. Click"Reject" to deny access

3.3 User Profiles
• View user details on the Users page
• See user roles and approval status
• Track when users were approved and by whom

========================================
4. SAMPLE MANAGEMENT
========================================

4.1 Adding New Samples
1. Navigate to the Samples page
2. Click "Add Sample" button
3. Fill in required information:
   - Sample ID
   - Sample Type
   - Collection Date
   - Storage Location (Dewar)
   - Additional metadata
4. Click "Save"

4.2 Searching Samples
• Use the search bar to find specific samples
• Filter by sample type, date, or location
• Sort results by various criteria

4.3 Editing Samples
1. Locate the sample in the list
2. Click "Edit" button
3. Modify the information
4. Click "Save Changes"

4.4 Sample Status Tracking
• Active: Currently stored
• Retrieved: Removed from storage
• Discarded: Properly disposed

========================================
5. DEWAR MANAGEMENT
========================================

5.1 Creating New Dewars
1. Go to the Dewars page
2. Click "Add Dewar" button
3. Enter dewar information:
   - Dewar ID/Name
   - Location
   - Capacity
   - Temperature setting
4. Click "Create"

5.2 Managing Dewar Contents
• View all samples in each dewar
• Monitor capacity and utilization
• Track temperature and maintenance logs

5.3 Dewar Status
• Active: In use
• Maintenance: Under service
• Retired: No longer in use

========================================
6. REPORTS
========================================

6.1 Available Reports
• Sample Inventory Report
• Dewar Utilization Report
• User Activity Report
• Storage Capacity Analysis

6.2 Generating Reports
1. Navigate to Reports page
2. Select report type
3. Choose date range if applicable
4. Click "Generate Report"
5. View or download results

6.3 Exporting Data
• Export reports as Excel files
• Download comprehensive data exports
• Schedule automated reports (admin only)

========================================
7. SETTINGS
========================================

7.1 Profile Settings
• Update your personal information
• Change password
• Set notification preferences

7.2 System Settings (Admin Only)
• Configure system-wide parameters
• Manage backup schedules
• Set data retention policies

========================================
8. TROUBLESHOOTING
========================================

8.1 Login Issues
Problem: Cannot log in
Solution:
• Verify your email and password
• Check if your account is approved
• Clear browser cache and cookies
• Contact administrator

8.2 Permission Denied Errors
Problem: "Permission denied for table users" error
Solution:
• This indicates you need administrator approval
• Contact your lab administrator
• Ensure you're logged in with correct credentials

8.3 Data Not Appearing
Problem: Samples or dewars not showing
Solution:
• Refresh the page
• Check your filter settings
• Verify you have proper permissions
• Contact support if issue persists

========================================
9. SUPPORT
========================================

For additional help:
• Contact your laboratory administrator
• Submit a support ticket through the system
• Refer to this manual for common procedures
• Check for system updates and announcements

========================================
SECURITY BEST PRACTICES
========================================
• Use strong, unique passwords
• Log out when finished
• Don't share account credentials
• Report suspicious activity immediately
• Keep your contact information current

========================================
END OF USER MANUAL
========================================

© 2025 CryoTracker Lab Management System
All rights reserved.
    `;

    // Create PDF using jsPDF
    const doc = new jsPDF();
    
    // Set font size and style
    doc?.setFontSize(10);
    
    // Split content into lines and add to PDF
    const lines = doc?.splitTextToSize(manualContent, 180);
    let yPosition = 20;
    const pageHeight = doc?.internal?.pageSize?.height;
    const lineHeight = 7;
    
    lines?.forEach((line) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 20) {
        doc?.addPage();
        yPosition = 20;
      }
      
      doc?.text(line, 15, yPosition);
      yPosition += lineHeight;
    });
    
    // Save the PDF
    doc?.save('CryoTracker_User_Manual.pdf');
  };

  const sections = [
    {
      icon: BookOpen,
      title: 'Getting Started',
      description: 'Learn how to register, get approved, and start using CryoTracker',
      items: [
        'Account registration process',
        'Waiting for administrator approval',
        'First login and dashboard overview',
        'Understanding user roles and permissions'
      ]
    },
    {
      icon: Users,
      title: 'User Management',
      description: 'Manage users and understand the approval system',
      items: [
        'User roles: Administrator vs Regular User',
        'Approval workflow for new users',
        'Viewing and managing user profiles',
        'Understanding pending approval status'
      ]
    },
    {
      icon: TestTube,
      title: 'Sample Management',
      description: 'Track and organize your laboratory samples',
      items: [
        'Adding new samples to the system',
        'Searching and filtering samples',
        'Editing sample information',
        'Tracking sample status and location'
      ]
    },
    {
      icon: Package,
      title: 'Dewar Management',
      description: 'Manage cryogenic storage containers',
      items: [
        'Creating and configuring dewars',
        'Monitoring capacity and utilization',
        'Tracking dewar contents',
        'Managing dewar status and maintenance'
      ]
    },
    {
      icon: FileText,
      title: 'Reports & Analytics',
      description: 'Generate reports and analyze data',
      items: [
        'Sample inventory reports',
        'Dewar utilization analysis',
        'User activity tracking',
        'Exporting data to Excel'
      ]
    },
    {
      icon: Lock,
      title: 'Security & Access',
      description: 'Understanding permissions and security',
      items: [
        'Password security best practices',
        'Understanding approval requirements',
        'Administrator verification process',
        'Troubleshooting permission errors'
      ]
    }
  ];

  const faqs = [
    {
      question: 'What does "pending approval" mean?',
      answer: 'When you register, your account is created but not yet active. An administrator must review and approve your request before you can access the full system features.'
    },
    {
      question: 'How will a user be approved?',
      answer: 'Administrators can approve users by navigating to the Users page, selecting the "Pending Approval" tab, and clicking the "Approve" button next to your account. Once approved, you\'ll have full access to create dewars and manage samples.'
    },
    {
      question: 'How is an administrator verified?',
      answer: 'The first user who registers automatically becomes an approved administrator. Subsequent administrators must be approved by an existing approved administrator. The system tracks who approved whom through the approved_by field.'
    },
    {
      question: 'What if I get a "permission denied" error?',
      answer: 'This usually means your account is still pending approval. Contact your laboratory administrator to approve your account. Only approved users can access most system features.'
    },
    {
      question: 'Can I approve other users?',
      answer: 'Only users with administrator role AND approved status can approve other users. Regular users cannot approve accounts, even if they themselves are approved.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-8 mb-8 text-white">
            <h1 className="text-3xl font-bold mb-4">CryoTracker User Manual</h1>
            <p className="text-blue-100 mb-6">
              Comprehensive guide to using the CryoTracker laboratory sample tracking system
            </p>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors shadow-md"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Manual (PDF)
            </button>
          </div>

          {/* Quick Start Guide */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Start Guide</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold">
                    1
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sign Up</h3>
                  <p className="text-gray-600">
                    Create your account with email and password
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold">
                    2
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Get Approved</h3>
                  <p className="text-gray-600">
                    Wait for administrator to approve your account
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold">
                    3
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Tracking</h3>
                  <p className="text-gray-600">
                    Begin managing samples and dewars
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Sections */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {sections?.map((section, index) => {
              const Icon = section?.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start mb-4">
                    <Icon className="w-8 h-8 text-blue-600 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {section?.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{section?.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {section?.items?.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* FAQs */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs?.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {faq?.question}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{faq?.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Support Section */}
          <div className="bg-blue-50 rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Need More Help?</h2>
            <p className="text-gray-700 mb-4">
              If you have questions not covered in this manual:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                Contact your laboratory administrator
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                Download the complete manual for offline reference
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                Check system announcements for updates
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;